/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BudgetRecommendation } from '../models/BudgetRecommendation';
import type { CareerSiteLocationResponse } from '../models/CareerSiteLocationResponse';
import type { CareerSitePositionResponse } from '../models/CareerSitePositionResponse';
import type { CreateChannelUtmSourceMappingsRequest } from '../models/CreateChannelUtmSourceMappingsRequest';
import type { CreateChannelUtmSourceMappingsResponse } from '../models/CreateChannelUtmSourceMappingsResponse';
import type { EnableSourcingChannelsRequest } from '../models/EnableSourcingChannelsRequest';
import type { GeneralRecommendationResponse } from '../models/GeneralRecommendationResponse';
import type { HireStagesResponse } from '../models/HireStagesResponse';
import type { HiringGoalDetailsResponse } from '../models/HiringGoalDetailsResponse';
import type { HiringOpeningsResponse } from '../models/HiringOpeningsResponse';
import type { HistoricalConversionDataResponse } from '../models/HistoricalConversionDataResponse';
import type { NonIntegratedSourcingChannelsResponse } from '../models/NonIntegratedSourcingChannelsResponse';
import type { OpeningAtRiskResponse } from '../models/OpeningAtRiskResponse';
import type { OpeningDetailResponse } from '../models/OpeningDetailResponse';
import type { OverlappingSourcingPurchaseResponse } from '../models/OverlappingSourcingPurchaseResponse';
import type { Pagination } from '../models/Pagination';
import type { ReachCampaignResponse } from '../models/ReachCampaignResponse';
import type { SourcingAggregateSpendResponse } from '../models/SourcingAggregateSpendResponse';
import type { SourcingAnalytics } from '../models/SourcingAnalytics';
import type { SourcingBrandsResponse } from '../models/SourcingBrandsResponse';
import type { SourcingCampaignsIndexResponse } from '../models/SourcingCampaignsIndexResponse';
import type { SourcingCampaignsMetricsResponse } from '../models/SourcingCampaignsMetricsResponse';
import type { SourcingChannels } from '../models/SourcingChannels';
import type { SourcingChannelStatus } from '../models/SourcingChannelStatus';
import type { SourcingMetricsFilterRange } from '../models/SourcingMetricsFilterRange';
import type { SourcingOpeningAnalyticsResponse } from '../models/SourcingOpeningAnalyticsResponse';
import type { SourcingOpeningChannelsResponse } from '../models/SourcingOpeningChannelsResponse';
import type { SourcingOpeningDetailsResponse } from '../models/SourcingOpeningDetailsResponse';
import type { SourcingOpenings } from '../models/SourcingOpenings';
import type { SourcingOpeningsPayload } from '../models/SourcingOpeningsPayload';
import type { SourcingOpeningsResponse } from '../models/SourcingOpeningsResponse';
import type { SourcingOpeningUpdateParameters } from '../models/SourcingOpeningUpdateParameters';
import type { SourcingPerformanceStatusMetrics } from '../models/SourcingPerformanceStatusMetrics';
import type { SourcingPurchaseBulkCreateRequest } from '../models/SourcingPurchaseBulkCreateRequest';
import type { SourcingPurchaseBulkCreateResponse } from '../models/SourcingPurchaseBulkCreateResponse';
import type { SourcingPurchaseDetails } from '../models/SourcingPurchaseDetails';
import type { SourcingPurchaseParameters } from '../models/SourcingPurchaseParameters';
import type { SourcingSetting } from '../models/SourcingSetting';
import type { SourcingSettingUpdateParameters } from '../models/SourcingSettingUpdateParameters';
import type { SourcingSnapshots } from '../models/SourcingSnapshots';
import type { SourcingStageStatsResponse } from '../models/SourcingStageStatsResponse';
import type { SourcingStageType } from '../models/SourcingStageType';
import type { UtmSourceMappingKeysResponse } from '../models/UtmSourceMappingKeysResponse';
import type { VonqChannels } from '../models/VonqChannels';
import type { VonqContract } from '../models/VonqContract';
import type { VonqContractChannelDetails } from '../models/VonqContractChannelDetails';
import type { VonqContractPostingRequirements } from '../models/VonqContractPostingRequirements';
import type { VonqContracts } from '../models/VonqContracts';
import type { VonqPostingRequirementAutocompleteResponse } from '../models/VonqPostingRequirementAutocompleteResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class SourcingService {

    /**
     * Requests sourcing brands
     * Return sourcing brands for the logged in user
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @param query Filter brands returned by matching address
     * @param isSelectedForSourcing Filter brands returned by matching is_selected_for_sourcing
     * @returns SourcingBrandsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingBrands(
        page?: number,
        perPage?: number,
        query?: string,
        isSelectedForSourcing?: boolean,
    ): CancelablePromise<SourcingBrandsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/brands',
            query: {
                'page': page,
                'per_page': perPage,
                'query': query,
                'is_selected_for_sourcing': isSelectedForSourcing,
            },
        });
    }

    /**
     * Read a Reach campaign breakdown
     * @param id Sourcing purchase external ID
     * @param dateRange
     * @param startDate
     * @param endDate
     * @returns any breakdown or unavailable
     * @throws ApiError
     */
    public static getInternalApiSourcingCampaignsReachBreakdown(
        id: string,
        dateRange?: SourcingMetricsFilterRange,
        startDate?: string,
        endDate?: string,
    ): CancelablePromise<{
        state: 'ok' | 'unavailable';
        ad_rows: Array<{
            position: number;
            ad_type: 'google_search' | 'google_pmax' | 'google_demand_gen' | 'meta' | 'unknown';
            label: string | null;
            provider: 'google' | 'meta' | null;
            status: 'active' | 'stopped' | 'completed' | 'scheduled';
            start_date: string;
            end_date: string | null;
            budget: number | null;
            budget_type: string | null;
            spend: number | null;
            applications: number | null;
            hires: number | null;
            cpa: number | null;
            cph: number | null;
            conversion: number | null;
        }>;
        other_row: {
            applications: number | null;
            hires: number | null;
            conversion: number | null;
        } | null;
        totals: {
            applications: number | null;
            hires: number | null;
            cpa: number | null;
            cph: number | null;
            conversion: number | null;
            spend: number;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/campaigns/{id}/reach_breakdown',
            path: {
                'id': id,
            },
            query: {
                'date_range': dateRange,
                'start_date': startDate,
                'end_date': endDate,
            },
            errors: {
                401: `Source access required`,
                404: `breakdown disabled or purchase unavailable`,
                422: `invalid date range`,
            },
        });
    }

    /**
     * List sourcing campaigns
     * @param dateRange Preset metrics window. Default / `lifetime` uses all available history. start_date and end_date are only considered when this is empty.
     * @param startDate Custom window start; only used when date_range is empty
     * @param endDate Custom window end; only used when date_range is empty
     * @param channels
     * @param statuses
     * @param positions
     * @param locations
     * @param brands
     * @param openings
     * @param search
     * @param page
     * @param perPage
     * @param sortBy
     * @param sortDir
     * @param hiresToDate Count hires among the custom range applicants through now instead of through end_date. Ignored for a preset or lifetime. Defaults to false.
     * @returns SourcingCampaignsIndexResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingCampaigns(
        dateRange?: SourcingMetricsFilterRange,
        startDate?: string,
        endDate?: string,
        channels?: Array<string>,
        statuses?: Array<SourcingChannelStatus>,
        positions?: Array<string>,
        locations?: Array<string>,
        brands?: Array<string>,
        openings?: Array<string>,
        search?: string,
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDir?: 'asc' | 'desc',
        hiresToDate?: boolean,
    ): CancelablePromise<SourcingCampaignsIndexResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/campaigns',
            query: {
                'date_range': dateRange,
                'start_date': startDate,
                'end_date': endDate,
                'channels[]': channels,
                'statuses[]': statuses,
                'positions[]': positions,
                'locations[]': locations,
                'brands[]': brands,
                'openings[]': openings,
                'search': search,
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_dir': sortDir,
                'hires_to_date': hiresToDate,
            },
            errors: {
                422: `incomplete custom dates when date_range is empty`,
            },
        });
    }

    /**
     * Aggregate sourcing campaign overview metrics
     * @param dateRange Preset metrics window. Default / `lifetime` uses all available history. start_date and end_date are only considered when this is empty.
     * @param startDate Custom window start; only used when date_range is empty
     * @param endDate Custom window end; only used when date_range is empty
     * @param channels
     * @param statuses
     * @param positions
     * @param locations
     * @param brands
     * @param openings
     * @param search
     * @param hiresToDate Count hires among the custom range applicants through now instead of through end_date. Ignored for a preset or lifetime. Defaults to false.
     * @returns SourcingCampaignsMetricsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingCampaignsMetrics(
        dateRange?: SourcingMetricsFilterRange,
        startDate?: string,
        endDate?: string,
        channels?: Array<string>,
        statuses?: Array<SourcingChannelStatus>,
        positions?: Array<string>,
        locations?: Array<string>,
        brands?: Array<string>,
        openings?: Array<string>,
        search?: string,
        hiresToDate?: boolean,
    ): CancelablePromise<SourcingCampaignsMetricsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/campaigns/metrics',
            query: {
                'date_range': dateRange,
                'start_date': startDate,
                'end_date': endDate,
                'channels[]': channels,
                'statuses[]': statuses,
                'positions[]': positions,
                'locations[]': locations,
                'brands[]': brands,
                'openings[]': openings,
                'search': search,
                'hires_to_date': hiresToDate,
            },
            errors: {
                422: `incomplete custom dates when date_range is empty`,
            },
        });
    }

    /**
     * Fetch Indeed CPA prediction for an opening
     * Predicts cost-per-applicant (CPA) for Indeed for this opening. Use when building a sourcing plan, comparing Indeed to other channels, or deciding whether to recommend or increase Indeed spend.
     * @param openingSlug Opening slug or external_id
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsIndeedCpaPrediction(
        openingSlug: string,
    ): CancelablePromise<{
        opening_id: string;
        channel: string;
        cpa: number;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/indeed_cpa_prediction',
            path: {
                'opening_slug': openingSlug,
            },
            errors: {
                404: `opening not found`,
                422: `Indeed CPA prediction unavailable`,
            },
        });
    }

    /**
     * Fetch Joveo CPA prediction for an opening
     * Predicts the minimum cost-per-applicant (CPA) for Joveo for this opening. Use when building a sourcing plan, comparing Joveo to other channels, or deciding whether to recommend or increase Joveo spend.
     * @param openingSlug Opening slug or external_id
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsJoveoCpaPrediction(
        openingSlug: string,
    ): CancelablePromise<{
        opening_id: string;
        channel: string;
        cpa: number;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/joveo_cpa_prediction',
            path: {
                'opening_slug': openingSlug,
            },
            errors: {
                404: `opening not found`,
                422: `Joveo CPA prediction unavailable`,
            },
        });
    }

    /**
     * Fetch channel CPA benchmarks for an opening
     * Returns historical channel performance benchmarks for this opening's role and location, including total applications, hires, spend, CPA, CPH, and conversion-to-hire. Use when ranking channels, estimating cost or hire outcomes without a live prediction, or checking whether a channel typically performs well for similar openings. Optionally pass channel_names[] to limit the lookup; otherwise all publishable channels for the account are included.
     * @param openingSlug Opening slug or external_id
     * @param channelNames Optional list of sourcing channel names to look up. Defaults to all publishable channels for the account.
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsCpaBenchmarks(
        openingSlug: string,
        channelNames?: Array<string>,
    ): CancelablePromise<{
        opening_id: string;
        soc_code: string | null;
        benchmarks: Record<string, {
            total_applications: number | null;
            hires: number | null;
            spend: number | null;
            cpa: number | null;
            cph: number | null;
            conversion_to_hire: number | null;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/cpa_benchmarks',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'channel_names[]': channelNames,
            },
            errors: {
                404: `opening not found`,
            },
        });
    }

    /**
     * Requests sourcing openings
     * Return sourcing opening for the logged in user
     * @param selectedForSourcing Filter openings by is_selected_for_sourcing
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @param sortBy field to sort results by
     * @param sortDirection direction to sort results
     * @param query Filter openings returned by matching opening/location/position name
     * @param positionIds Filter openings returned by matching position_ids
     * @param locationIds Filter openings returned by matching location_ids
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @param brandIds Filter openings returned by matching brand_ids
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardOpenings(
        selectedForSourcing?: boolean,
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDirection?: 'asc' | 'desc',
        query?: string,
        positionIds?: Array<string>,
        locationIds?: Array<string>,
        showAggregateData?: boolean,
        brandIds?: Array<string>,
    ): CancelablePromise<{
        default_metrics_period: number | null;
        data: Array<SourcingOpenings>;
        pagination: Pagination;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/openings',
            query: {
                'selected_for_sourcing': selectedForSourcing,
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
                'query': query,
                'position_ids[]': positionIds,
                'location_ids[]': locationIds,
                'show_aggregate_data': showAggregateData,
                'brand_ids[]': brandIds,
            },
        });
    }

    /**
     * Sourcing analytics
     * Return sourcing analytics for a date range
     * @param fromDate Start date
     * @param toDate End date
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardAnalytics(
        fromDate: string,
        toDate: string,
    ): CancelablePromise<{
        analytics: SourcingAnalytics;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/analytics',
            query: {
                'from_date': fromDate,
                'to_date': toDate,
            },
        });
    }

    /**
     * Sourcing snapshots
     * Return sourcing snapshots
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardSnapshots(
        showAggregateData?: boolean,
    ): CancelablePromise<{
        snapshots: SourcingSnapshots;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/snapshots',
            query: {
                'show_aggregate_data': showAggregateData,
            },
        });
    }

    /**
     * Openings at risk count
     * Return openings at risk count
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardOpeningsAtRiskCount(): CancelablePromise<{
        openings_at_risk: number;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/openings_at_risk_count',
        });
    }

    /**
     * List openings at risk with metrics
     * Return paginated openings at risk with metrics
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @returns OpeningAtRiskResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardOpeningsAtRisk(
        page?: number,
        perPage?: number,
    ): CancelablePromise<OpeningAtRiskResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/openings_at_risk',
            query: {
                'page': page,
                'per_page': perPage,
            },
        });
    }

    /**
     * Sourcing Recommendation
     * Return non sourced opening
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardSourcingRecommendation(): CancelablePromise<{
        opening_ids: Array<string>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/sourcing_recommendation',
        });
    }

    /**
     * Automation Recommendation
     * Return whether automation enabled
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingDashboardAutomationRecommendation(): CancelablePromise<{
        automation_enabled: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/dashboard/automation_recommendation',
        });
    }

    /**
     * Update openings for sourcing
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiSourcingOpeningsMarkForSourcing(
        requestBody: SourcingOpeningsPayload,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/openings/mark_for_sourcing',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `unprocessable entity`,
            },
        });
    }

    /**
     * Add openings to source dashboard
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiSourcingOpeningsAddOpeningsToSourceDashboard(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/openings/add_openings_to_source_dashboard',
            errors: {
                422: `unprocessable entity`,
            },
        });
    }

    /**
     * Update Hiring Goal Attributes
     * @param openingSlug Opening slug or external_id
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingDashboard(
        openingSlug: string,
        requestBody: {
            budget_allotted?: number;
            expected_conversion?: number;
            expected_cpa?: number;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/dashboard/{opening_slug}',
            path: {
                'opening_slug': openingSlug,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `unprocessable entity`,
            },
        });
    }

    /**
     * Requests sourcing locations
     * Return sourcing locations for the logged in user
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @param sortBy field to sort results by
     * @param sortDirection direction to sort results
     * @param query Filter locations returned by matching address
     * @param isSelectedForSourcing Is added to sourcing dashboard
     * @returns CareerSiteLocationResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingLocations(
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDirection?: 'asc' | 'desc',
        query?: string,
        isSelectedForSourcing?: boolean,
    ): CancelablePromise<CareerSiteLocationResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/locations',
            query: {
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
                'query': query,
                'is_selected_for_sourcing': isSelectedForSourcing,
            },
        });
    }

    /**
     * Requests suggested target budget for an opening
     * Return suggested target budget
     * @param openingSlug Opening slug or external_id
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsSuggestedTargetBudget(
        openingSlug: string,
    ): CancelablePromise<{
        target_budget: number;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/suggested_target_budget',
            path: {
                'opening_slug': openingSlug,
            },
        });
    }

    /**
     * Requests hiring goal and sourcing details for an opening
     * Return sourcing opening details
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @param showAggregateBudgetSpent whether to show aggregate budget spent
     * @param startDate Inclusive start of the metrics window (ISO 8601 date). Limits channel metrics (spend, applicants, etc.) from this date onward. When set (alone or with end_date), hiring goal dates are ignored for the metrics range.
     * @param endDate Inclusive end of the metrics window (ISO 8601 date). Limits channel metrics through this date. If omitted, metrics run through the current date in the opening time zone.
     * @param filterRange
     * @returns SourcingOpeningDetailsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpenings(
        openingSlug: string,
        hiringGoalId?: string,
        showAggregateData?: boolean,
        showAggregateBudgetSpent?: boolean,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<SourcingOpeningDetailsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'show_aggregate_data': showAggregateData,
                'show_aggregate_budget_spent': showAggregateBudgetSpent,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
            errors: {
                404: `error`,
            },
        });
    }

    /**
     * Requests metrics for an opening
     * Return the metrics cards and hiring goal metrics for an opening
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @param showAggregateBudgetSpent whether to show aggregate budget spent
     * @param startDate Inclusive start of the metrics window (ISO 8601 date). When set (alone or with end_date), hiring goal dates are ignored for the metrics range.
     * @param endDate Inclusive end of the metrics window (ISO 8601 date). If omitted, metrics run through the current date in the opening time zone.
     * @param filterRange
     * @returns SourcingOpeningAnalyticsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsAnalytics(
        openingSlug: string,
        hiringGoalId?: string,
        showAggregateData?: boolean,
        showAggregateBudgetSpent?: boolean,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<SourcingOpeningAnalyticsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/analytics',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'show_aggregate_data': showAggregateData,
                'show_aggregate_budget_spent': showAggregateBudgetSpent,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
        });
    }

    /**
     * Update Opening
     * @param openingExternalId Opening slug or external_id
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static putInternalApiSourcingOpenings(
        openingExternalId: string,
        requestBody: SourcingOpeningUpdateParameters,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/internal_api/sourcing/openings/{opening_external_id}',
            path: {
                'opening_external_id': openingExternalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `job not found`,
            },
        });
    }

    /**
     * Requests sourcing channels for an opening
     * Return sourcing opening channels details
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @param showAggregateBudgetSpent whether to show aggregate budget spent
     * @param startDate Inclusive start of the metrics window (ISO 8601 date). Limits channel metrics (spend, applicants, etc.) from this date onward. When set (alone or with end_date), hiring goal dates are ignored for the metrics range.
     * @param endDate Inclusive end of the metrics window (ISO 8601 date). Limits channel metrics through this date. If omitted, metrics run through the current date in the opening time zone.
     * @param filterRange
     * @returns SourcingOpeningChannelsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsSourcingChannels(
        openingSlug: string,
        hiringGoalId?: string,
        showAggregateData?: boolean,
        showAggregateBudgetSpent?: boolean,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<SourcingOpeningChannelsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/sourcing_channels',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'show_aggregate_data': showAggregateData,
                'show_aggregate_budget_spent': showAggregateBudgetSpent,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
        });
    }

    /**
     * Requests stats for sourcing stages for an opening
     * Return sourcing stage stats
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @param showAggregateBudgetSpent whether to show aggregate budget spent
     * @param startDate Start date of the metrics window (ISO 8601 date)
     * @param endDate End date of the metrics window (ISO 8601 date)
     * @param filterRange Filter range for the metrics
     * @returns SourcingStageStatsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsSourcingStageStats(
        openingSlug: string,
        hiringGoalId?: string,
        showAggregateData?: boolean,
        showAggregateBudgetSpent?: boolean,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<SourcingStageStatsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/sourcing_stage_stats',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'show_aggregate_data': showAggregateData,
                'show_aggregate_budget_spent': showAggregateBudgetSpent,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
        });
    }

    /**
     * Requests sourcing channels as recommendation
     * Return recommended sourcing channels list
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @returns GeneralRecommendationResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsGeneralRecommendations(
        openingSlug: string,
        hiringGoalId?: string,
    ): CancelablePromise<GeneralRecommendationResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/general_recommendations',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
            },
        });
    }

    /**
     * Returns active budget recommendation for the hiring goal
     * Returns the active sourcing purchase recommendation budget, or null when none is active
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @returns BudgetRecommendation successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsBudgetRecommendation(
        openingSlug: string,
        hiringGoalId?: string,
    ): CancelablePromise<BudgetRecommendation> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/budget_recommendation',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
            },
        });
    }

    /**
     * Maps sourcing stages to hire stages
     * @param openingSlug Opening slug or external_id
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingOpeningsMapSourcingStages(
        openingSlug: string,
        requestBody: {
            stages: Array<{
                stage_type: SourcingStageType;
                title: string;
                stage_id: string;
                expected_conversion: number | null;
                is_automated: boolean;
            }>;
        },
    ): CancelablePromise<{
        message: string;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/openings/{opening_slug}/map_sourcing_stages',
            path: {
                'opening_slug': openingSlug,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `opening not found`,
                422: `validation errors`,
            },
        });
    }

    /**
     * Requests hire stages for a given opening
     * Return stages with the respective conversion data
     * @param openingSlug Opening slug or external_id
     * @returns HireStagesResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsHireStages(
        openingSlug: string,
    ): CancelablePromise<HireStagesResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/hire_stages',
            path: {
                'opening_slug': openingSlug,
            },
        });
    }

    /**
     * Requests historical conversion data
     * Return historical conversion data
     * @param openingSlug Opening slug or external_id
     * @param channel channel slug
     * @returns HistoricalConversionDataResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsHistoricalConversionData(
        openingSlug: string,
        channel?: string,
    ): CancelablePromise<HistoricalConversionDataResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/historical_conversion_data',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'channel': channel,
            },
        });
    }

    /**
     * Requests sourcing funnels for an opening
     * Return sourcing funnels details
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @param showAggregateBudgetSpent whether to show aggregate budget spent
     * @param startDate Start date of the metrics window (ISO 8601 date)
     * @param endDate End date of the metrics window (ISO 8601 date)
     * @param filterRange Filter range for the metrics
     * @returns SourcingOpeningsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsSourcingFunnels(
        openingSlug: string,
        hiringGoalId?: string,
        showAggregateData?: boolean,
        showAggregateBudgetSpent?: boolean,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<SourcingOpeningsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/sourcing_funnels',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'show_aggregate_data': showAggregateData,
                'show_aggregate_budget_spent': showAggregateBudgetSpent,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
        });
    }

    /**
     * Requests aggregate spend of sourcing funnels for an opening
     * Return aggregate spend of sourcing funnels
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @param startDate Start date of the metrics window (ISO 8601 date)
     * @param endDate End date of the metrics window (ISO 8601 date)
     * @param filterRange Filter range for the metrics
     * @returns SourcingAggregateSpendResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsAggregateSpend(
        openingSlug: string,
        hiringGoalId?: string,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<SourcingAggregateSpendResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/aggregate_spend',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
        });
    }

    /**
     * Requests hiring openings for a given opening
     * Return hiring openings with hire counts.
     * @param openingSlug Opening slug or external_id
     * @param hiringGoalId External ID of the Hiring goal
     * @param startDate Start date of the metrics window (ISO 8601 date)
     * @param endDate End date of the metrics window (ISO 8601 date)
     * @param filterRange Filter range for the metrics
     * @returns HiringOpeningsResponse successful with hiring openings
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsHiringOpenings(
        openingSlug: string,
        hiringGoalId?: string,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<HiringOpeningsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/hiring_openings',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
            errors: {
                404: `error`,
            },
        });
    }

    /**
     * Request for Opening Details
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiSourcingOpeningsOpeningsByExternalIds(
        requestBody: {
            opening_external_ids: Array<string>;
        },
    ): CancelablePromise<{
        openings: Array<OpeningDetailResponse>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/openings/openings_by_external_ids',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Requests hiring goals for a given opening
     * Return hiring goals with analytics
     * @param openingSlug Opening slug or external_id
     * @param page page number in result set
     * @param scope Type of Hiring Goal
     * @returns HiringGoalDetailsResponse successful with hiring goals
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsHiringGoals(
        openingSlug: string,
        page?: number,
        scope?: 'active' | 'past' | 'scheduled',
    ): CancelablePromise<HiringGoalDetailsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/hiring_goals',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'page': page,
                'scope': scope,
            },
            errors: {
                404: `error`,
            },
        });
    }

    /**
     * Requests status metrics of the opening
     * Returns status metrics of the opening
     * @param openingSlug slug of the opening
     * @param hiringGoalId External ID of the Hiring goal
     * @param showAggregateData whether to show aggregate or funnel data in the result
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingOpeningsPerformanceStatusMetrics(
        openingSlug: string,
        hiringGoalId?: string,
        showAggregateData?: boolean,
    ): CancelablePromise<{
        performance_status_metrics: SourcingPerformanceStatusMetrics;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/openings/{opening_slug}/performance_status_metrics',
            path: {
                'opening_slug': openingSlug,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'show_aggregate_data': showAggregateData,
            },
        });
    }

    /**
     * Requests sourcing positions
     * Return sourcing positions for the logged in user
     * @param page page number in result set
     * @param perPage number of results to return per page
     * @param sortBy field to sort results by
     * @param sortDirection direction to sort results
     * @param query Filter positions returned by matching name
     * @param isSelectedForSourcing Is added to sourcing dashboard
     * @returns CareerSitePositionResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingPositions(
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDirection?: 'asc' | 'desc',
        query?: string,
        isSelectedForSourcing?: boolean,
    ): CancelablePromise<CareerSitePositionResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/positions',
            query: {
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
                'query': query,
                'is_selected_for_sourcing': isSelectedForSourcing,
            },
        });
    }

    /**
     * Creates or updates channel UTM source mappings
     * Creates a new sourcing channel (by name) or maps an existing channel (by channel_id) to the given utm_sources. Returns the channel with its utm_sources (mapping_key_id, value, applicant_count).
     * @param requestBody
     * @returns CreateChannelUtmSourceMappingsResponse created
     * @throws ApiError
     */
    public static postInternalApiSourcingSourcingChannels(
        requestBody: CreateChannelUtmSourceMappingsRequest,
    ): CancelablePromise<CreateChannelUtmSourceMappingsResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/sourcing_channels',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `unprocessable (e.g. channel_name blank when channel_id omitted)`,
            },
        });
    }

    /**
     * Requests sourcing channels
     * Return sourcing channels of the account
     * @param openingIds opening external ids
     * @param sortBy sort by list_price.desc or list_price.asc
     * @param section fetch only the requested channel section; other sections are returned empty
     * @param name filter by name of the channel
     * @param offset offset into the paid boards list; used to fetch the next page
     * @returns SourcingChannels successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingChannels(
        openingIds?: Array<string>,
        sortBy?: 'list_price.desc' | 'list_price.asc',
        section?: 'popular' | 'free' | 'paid',
        name?: string,
        offset?: number,
    ): CancelablePromise<SourcingChannels> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_channels',
            query: {
                'opening_ids[]': openingIds,
                'sort_by': sortBy,
                'section': section,
                'name': name,
                'offset': offset,
            },
        });
    }

    /**
     * Removes account sourcing channel (destroy)
     * @param id Sourcing channel external_id to remove from the account
     * @returns any Sourcing channel successfully removed
     * @throws ApiError
     */
    public static deleteInternalApiSourcingSourcingChannels(
        id: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/sourcing/sourcing_channels/{id}',
            path: {
                'id': id,
            },
            errors: {
                404: `Sourcing channel not found`,
            },
        });
    }

    /**
     * Returns all the sourcing channels available for the account
     * Returns all the sourcing channels available for the account
     * @returns SourcingChannels successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingChannelsAllChannels(): CancelablePromise<SourcingChannels> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_channels/all_channels',
        });
    }

    /**
     * Returns sourcing channels enabled for the account
     * Returns only sourcing channels that are enabled for the account (those with an account_sourcing_channel)
     * @param query Filter channels returned by matching name
     * @returns SourcingChannels successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingChannelsEnabledChannels(
        query?: string,
    ): CancelablePromise<SourcingChannels> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_channels/enabled_channels',
            query: {
                'query': query,
            },
        });
    }

    /**
     * Returns publishable non-integrated channels for the account
     * Returns publishable non-integrated channels for the account.
     * @returns NonIntegratedSourcingChannelsResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingChannelsNonIntegratedSourcingChannels(): CancelablePromise<NonIntegratedSourcingChannelsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_channels/non_integrated_sourcing_channels',
        });
    }

    /**
     * Enables sourcing channels for the account
     * Enable all available default sourcing channels or accept a list of sourcing channel slugs and returns the sourcing channels that are enabled for the account
     * @param requestBody
     * @returns SourcingChannels successful
     * @throws ApiError
     */
    public static postInternalApiSourcingSourcingChannelsEnableChannels(
        requestBody?: EnableSourcingChannelsRequest,
    ): CancelablePromise<SourcingChannels> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/sourcing_channels/enable_channels',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Update Sourcing Campaigns
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingSourcingPurchasesUpdateCampaignStatus(
        sourcingPurchaseId: string,
        requestBody: {
            enabled: boolean | null;
            channel: string | null;
            reach_campaign_id: string | null;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}/update_campaign_status',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Update Sourcing Purchase
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static putInternalApiSourcingSourcingPurchases(
        sourcingPurchaseId: string,
        requestBody: SourcingPurchaseParameters,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `sourcing purchase not found`,
                422: `validation errors`,
            },
        });
    }

    /**
     * Requests sourcing purchase and its campaign details
     * Return sourcing purchase details
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @param hiringGoalId External ID of the Hiring goal
     * @param startDate Start date of the metrics window (ISO 8601 date)
     * @param endDate End date of the metrics window (ISO 8601 date)
     * @param filterRange Filter range for the metrics
     * @returns SourcingPurchaseDetails successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingPurchases(
        sourcingPurchaseId: string,
        hiringGoalId?: string,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<SourcingPurchaseDetails> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
            errors: {
                404: `sourcing purchase not found`,
            },
        });
    }

    /**
     * Accept Sourcing Purchase Recommendation
     * Accept a budget recommendation for a sourcing purchase
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingSourcingPurchasesAcceptRecommendation(
        sourcingPurchaseId: string,
        requestBody?: {
            recommended_budget: number;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}/accept_recommendation',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `sourcing purchase not found`,
                422: `unable to accept recommendation`,
            },
        });
    }

    /**
     * Reject Sourcing Purchase Recommendation
     * Reject a budget recommendation for a sourcing purchase
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiSourcingSourcingPurchasesRejectRecommendation(
        sourcingPurchaseId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}/reject_recommendation',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            errors: {
                422: `unable to reject recommendation`,
            },
        });
    }

    /**
     * Get Sourcing Purchase Campaign Details
     * Returns campaign details for a sourcing purchase. Supported only for reach sourcing purchase
     * @param sourcingPurchaseId Sourcing Purchase external id
     * @param showAggregateData To show aggregate data or funnel data in the result
     * @param hiringGoalId External ID of the Hiring goal
     * @param startDate Start date of the metrics window (ISO 8601 date)
     * @param endDate End date of the metrics window (ISO 8601 date)
     * @param filterRange Filter range for the metrics
     * @returns ReachCampaignResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingPurchasesCampaignDetails(
        sourcingPurchaseId: string,
        showAggregateData: boolean,
        hiringGoalId?: string,
        startDate?: string,
        endDate?: string,
        filterRange?: SourcingMetricsFilterRange,
    ): CancelablePromise<ReachCampaignResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_purchases/{sourcing_purchase_id}/campaign_details',
            path: {
                'sourcing_purchase_id': sourcingPurchaseId,
            },
            query: {
                'hiring_goal_id': hiringGoalId,
                'show_aggregate_data': showAggregateData,
                'start_date': startDate,
                'end_date': endDate,
                'filter_range': filterRange,
            },
            errors: {
                404: `sourcing purchase not found`,
            },
        });
    }

    /**
     * Validate Sourcing Purchases
     * @param requestBody
     * @returns OverlappingSourcingPurchaseResponse successful
     * @throws ApiError
     */
    public static postInternalApiSourcingSourcingPurchasesValidateSourcingPurchases(
        requestBody: {
            channel_slug: string;
            start_date: string;
            end_date: string | null;
            opening_ids: Array<string>;
            is_vonq_channel: boolean | null;
            vonq_product_id: string | null;
            vonq_contract_id?: string | null;
            remote_channel_id?: string | null;
            posting_requirements?: any;
        },
    ): CancelablePromise<OverlappingSourcingPurchaseResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/sourcing_purchases/validate_sourcing_purchases',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `validation error`,
            },
        });
    }

    /**
     * Bulk Create Sourcing Purchases
     * Creates multiple sourcing purchases in a single transaction
     * @param requestBody
     * @returns SourcingPurchaseBulkCreateResponse successfully created
     * @throws ApiError
     */
    public static postInternalApiSourcingSourcingPurchasesBulkCreate(
        requestBody: SourcingPurchaseBulkCreateRequest,
    ): CancelablePromise<SourcingPurchaseBulkCreateResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/sourcing_purchases/bulk_create',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                422: `validation errors`,
            },
        });
    }

    /**
     * Get Pool Audiences
     * Retrieves a list of pool audiences for the current account
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingPurchasesPoolAudiences(): CancelablePromise<Array<{
        /**
         * Audience name
         */
        label: string;
        /**
         * Audience UUID
         */
        value: string;
    }>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_purchases/pool_audiences',
        });
    }

    /**
     * Retrieves sourcing settings
     * Retrieves sourcing settings for the account.
     * @returns SourcingSetting successful - returns existing settings
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingSettings(): CancelablePromise<SourcingSetting> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_settings',
        });
    }

    /**
     * Updates sourcing settings
     * Updates sourcing settings for the account.
     * @param requestBody
     * @returns SourcingSetting successful - updates multiple fields
     * @throws ApiError
     */
    public static patchInternalApiSourcingSourcingSettings(
        requestBody: SourcingSettingUpdateParameters,
    ): CancelablePromise<SourcingSetting> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/sourcing/sourcing_settings',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `not found - setting does not exist`,
                422: `unprocessable entity - validation failed`,
            },
        });
    }

    /**
     * Retrieves utm_source mapping keys
     * Returns utm_source mapping keys for the account.
     * @returns UtmSourceMappingKeysResponse successful
     * @throws ApiError
     */
    public static getInternalApiSourcingSourcingSettingsUtmSourceMappingKeys(): CancelablePromise<UtmSourceMappingKeysResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/sourcing_settings/utm_source_mapping_keys',
        });
    }

    /**
     * Requests Vonq contracts
     * Return vonq contracts of the account
     * @returns VonqContracts successful
     * @throws ApiError
     */
    public static getInternalApiSourcingVonqContracts(): CancelablePromise<VonqContracts> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/vonq/contracts',
        });
    }

    /**
     * Create VONQ Contract
     * Create new Vonq contract
     * @param requestBody
     * @returns any successfully created
     * @throws ApiError
     */
    public static postInternalApiSourcingVonqContractsNew(
        requestBody: {
            channel_id: string;
            channel_name: string;
            logo_rectangle?: string;
            logo_square?: string;
            alias?: string;
            expiry_date?: string;
            currency?: string;
            amount?: number;
            credits?: number;
            posting_duration_days?: number;
            credentials?: Record<string, string>;
            followed_instructions?: boolean;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/vonq/contracts',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `validation errors`,
            },
        });
    }

    /**
     * Requests Vonq channels
     * Return vonq channels of the account
     * @param search Filter Vonq channels based on search text
     * @returns VonqChannels successful
     * @throws ApiError
     */
    public static getInternalApiSourcingVonqContractsChannels(
        search: string,
    ): CancelablePromise<VonqChannels> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/vonq/contracts/channels',
            query: {
                'search': search,
            },
        });
    }

    /**
     * Requests Vonq channel details
     * Return vonq channel details
     * @param channelId Fetch Vonq channel based on channel id
     * @returns VonqContractChannelDetails successful
     * @throws ApiError
     */
    public static getInternalApiSourcingVonqContractsChannelDetails(
        channelId: number,
    ): CancelablePromise<VonqContractChannelDetails> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/vonq/contracts/channel_details',
            query: {
                'channel_id': channelId,
            },
        });
    }

    /**
     * Fetch Vonq posting requirements for contracts
     * Returns Vonq posting requirement field definitions for the given contract IDs in a single upstream request
     * @param contractIds
     * @returns VonqContractPostingRequirements successful
     * @throws ApiError
     */
    public static getInternalApiSourcingVonqContractsPostingRequirements(
        contractIds: Array<string>,
    ): CancelablePromise<Array<VonqContractPostingRequirements>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/sourcing/vonq/contracts/posting_requirements',
            query: {
                'contract_ids[]': contractIds,
            },
            errors: {
                400: `contract_ids missing or empty`,
                403: `contract_ids not accessible for account`,
            },
        });
    }

    /**
     * Fetch Vonq posting requirement autocomplete options
     * Returns autocomplete options for a Vonq contract posting requirement facet
     * @param channelIdOrContractId Numeric channel ID or Vonq contract UUID
     * @param postingRequirementName Case-sensitive posting requirement facet name
     * @param requestBody
     * @returns VonqPostingRequirementAutocompleteResponse successful
     * @throws ApiError
     */
    public static postInternalApiSourcingVonqContractsPostingRequirementsAutocomplete(
        channelIdOrContractId: string,
        postingRequirementName: string,
        requestBody?: any,
    ): CancelablePromise<VonqPostingRequirementAutocompleteResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/vonq/contracts/posting_requirements/{channel_id_or_contract_id}/{posting_requirement_name}/autocomplete',
            path: {
                'channel_id_or_contract_id': channelIdOrContractId,
                'posting_requirement_name': postingRequirementName,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `channel_id_or_contract_id not accessible for account`,
            },
        });
    }

    /**
     * Delete VONQ Contract
     * Remove a VONQ contract configuration tracking reference mapping from the account
     * @param id The ID of the account sourcing channel mapping record
     * @returns any successful deletion
     * @throws ApiError
     */
    public static deleteInternalApiSourcingVonqContractsReference(
        id: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/sourcing/vonq/contracts/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Creates a Vonq contract
     * @param requestBody
     * @returns VonqContract Vonq contract created
     * @throws ApiError
     */
    public static postInternalApiSourcingVonqContracts(
        requestBody: VonqContract,
    ): CancelablePromise<VonqContract> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/sourcing/vonq_contracts',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Unprocessable entity`,
            },
        });
    }

    /**
     * Deletes a Vonq contract
     * @param vonqContractId ID of the Vonq contract to delete
     * @returns any Contract successfully deleted
     * @throws ApiError
     */
    public static deleteInternalApiSourcingVonqContracts(
        vonqContractId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/sourcing/vonq_contracts/{vonq_contract_id}',
            path: {
                'vonq_contract_id': vonqContractId,
            },
            errors: {
                404: `Not found`,
            },
        });
    }

}